// Maps between the dashboard `Settlement` shape and the rentio-api-next
// `Settlement` DTOs. Single place that bridges groupId/unitId <-> propertyId
// and the JSON draftState/snapshot blobs, so components never change.

import {
  ICreateSettlement,
  ISettlement,
  IUpdateSettlement,
} from "@rentiohq/shared-frontend/dist/reduxV2/settlement";
import { toDay } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { toBrusselsIsoDay } from "./localDay.utils";
import {
  Settlement,
  SettlementDraftState,
  SettlementScope,
  SettlementSnapshot,
  SettlementStatus,
} from "./types";

// A settlement is tied to one property. group-scope -> the group property id
// lives in groupId; unit-scope -> the unit property id lives in unitId. Both
// are stringified real property ids.
const resolvePropertyId = (s: Pick<Settlement, "groupId" | "unitId">): number =>
  Number(s.groupId ?? s.unitId);

export const settlementToCreateDto = (
  s: Omit<Settlement, "id" | "createdAt">,
): ICreateSettlement => ({
  propertyId: resolvePropertyId(s),
  scope: s.scope,
  status: s.status,
  periodFrom: s.periodFrom,
  periodTo: s.periodTo,
  confirmedAt: s.confirmedAt,
  delayDays: s.delayDays,
  // draftState / snapshot are opaque JSON blobs the dashboard owns wholesale.
  draftState: s.draftState as any,
  snapshot: s.snapshot as any,
});

export const settlementPatchToUpdateDto = (
  patch: Partial<Settlement>,
): IUpdateSettlement => {
  const dto: IUpdateSettlement = {};

  if ("scope" in patch) dto.scope = patch.scope;
  if ("status" in patch) dto.status = patch.status;
  if ("periodFrom" in patch) dto.periodFrom = patch.periodFrom;
  if ("periodTo" in patch) dto.periodTo = patch.periodTo;
  if ("confirmedAt" in patch) dto.confirmedAt = patch.confirmedAt;
  if ("delayDays" in patch) dto.delayDays = patch.delayDays;
  // draftState is cleared (set to null) on confirmation, so map undefined->null.
  if ("draftState" in patch) dto.draftState = (patch.draftState ?? null) as any;
  if ("snapshot" in patch) dto.snapshot = (patch.snapshot ?? null) as any;

  return dto;
};

export const settlementFromApi = (s: ISettlement): Settlement => {
  const scope = s.scope as SettlementScope;
  const propertyId = String(s.propertyId);
  return {
    id: s.id,
    groupId: scope === "group" ? propertyId : undefined,
    unitId: scope === "unit" ? propertyId : undefined,
    scope,
    status: s.status as SettlementStatus,
    periodFrom: toDay(s.periodFrom) ?? "",
    periodTo: toDay(s.periodTo) ?? "",
    // The moment the draft was started, unlike the calendar days around it:
    // its UTC day is still yesterday just after midnight in Belgium.
    createdAt: toBrusselsIsoDay(s.createdAt) ?? "",
    confirmedAt: toDay(s.confirmedAt),
    delayDays: s.delayDays ?? 0,
    draftState: s.draftState as SettlementDraftState | undefined,
    snapshot: s.snapshot as SettlementSnapshot | undefined,
  };
};
